import { inject, Injectable, OnDestroy } from '@angular/core'
import { mergeWith, Observable, of, Subject, Subscription } from 'rxjs'
import { mergeMap, tap } from 'rxjs/operators'

import { Memoize } from 'typescript-memoize'

import { useAllArgs } from '@app/components/generic/utilities'
import { Confirmation } from '@app/components/logistics/confirmations/confirmations'
import { ConfirmationsApi } from '@app/components/logistics/confirmations/confirmations.api'
import { ConfirmationMethod, ConfirmationState } from '@app/components/logistics/confirmations/confirmations.constants'
import { SessionUser } from '@app/components/roles/user.utils'

import { UserService } from './user.service'

@Injectable({ providedIn: 'root' })
export class ConfirmationService implements OnDestroy {
  private readonly confirmationsApi = inject(ConfirmationsApi)
  private readonly userService = inject(UserService)

  private readonly userSubscription?: Subscription
  private readonly pendingConfirmations$ = new Subject<Confirmation>() // "Hot source" of Confirmations.
  private readonly pendingConfirmations: Confirmation[] = [] // "Cold source" of Confirmations.

  constructor () {
    this.userSubscription = this.userService.getUser().subscribe((user) => {
      if (!user.anonymous) {
        setTimeout(() => {
          this.loadConfirmationsForUser(user)
        }, 3000)
      }
    })
  }

  ngOnDestroy (): void {
    this.userSubscription?.unsubscribe()
  }

  @Memoize(useAllArgs)
  private loadConfirmationsForUser (user: SessionUser) {
    if (user.anonymous) return

    const params = {
      confirming_staff__email: user.email,
      state: ConfirmationState.WAITING,
      allowed_methods__in: [ConfirmationMethod.CORE_USER],
    }
    this.confirmationsApi.list(params).pipe(
      tap(_ => (this.pendingConfirmations.length = 0)), // Clear existing Confirmations.
      mergeMap(response => response.results), // For each Confirmation…
      tap(confirmation => this.addPendingConfirmation(confirmation)), // …add them to the existing confirmations.
    ).subscribe()
  }

  addPendingConfirmation (confirmation: Confirmation) {
    this.pendingConfirmations.push(confirmation)
    this.pendingConfirmations$.next(confirmation)
  }

  /**
   * Returns an Observable of pending Confirmations.
   * The Observable contains:
   * - the existing Confirmations (taken from the "cold source"),
   * - then the new ones from the "hot source".
   */
  public getPendingConfirmations (): Observable<Confirmation> {
    return of(...this.pendingConfirmations).pipe(mergeWith(this.pendingConfirmations$))
  }
}
